import { useState, useRef } from 'react';  
import { Button } from '../ui/button';  
import { Input } from '../ui/input';  
import { Label } from '../ui/label';  
import { Plus, Minus, Upload, Link as LinkIcon } from 'lucide-react';  
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';  

type Material = {  
  id?: string;  
  fileName: string;  
  link: string;  
  file?: File;  
}  

interface MaterialsListProps {  
  materials: Material[];  
  onMaterialsChange: (materials: Material[]) => void;  
}  

const MaterialsList = ({ materials, onMaterialsChange }: MaterialsListProps) => {  
  const fileInputRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});  
  
  const addMaterial = () => {  
    const newMaterial: Material = {  
      id: Date.now().toString(),
      fileName: '',  
      link: ''  
    };  
    
    onMaterialsChange([...materials, newMaterial]);  
  };  

  const removeMaterial = (id: string) => {  
    onMaterialsChange(materials.filter(material => material.id !== id));  
  };  

  const updateMaterial = (id: string, updates: Partial<Material>) => {  
    onMaterialsChange(  
      materials.map(material =>   
        material.id === id ? { ...material, ...updates } : material  
      )  
    );  
  };  

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, materialId: string) => {  
    const file = e.target.files?.[0];  
    if (file) {  
      updateMaterial(materialId, {   
        link: `../assets/${file.name}`, // Simulate a file path  
        fileName: file.name || 'Unnamed File',  
        file: file  
      });  
    }  
  };  

  return (  
    <div className="space-y-4 border rounded-lg p-4">  
      <div className="flex items-center justify-between">  
        <h3 className="text-lg font-semibold text-bytesplash-blue">Materials</h3>  
        <Button   
          type="button"   
          variant="outline"   
          size="sm"   
          onClick={addMaterial}  
        >  
          <Plus className="mr-1 h-4 w-4" />  
          Add Material  
        </Button>  
      </div>  
      
      {materials.map((material, index) => (  
        <div key={material.id} className="space-y-4 border-b pb-4 last:border-0">  
          <div className="flex items-center gap-2">  
            <span className="text-bytesplash-blue font-medium">  
              Material {index + 1}  
            </span>  
            <Button  
              type="button"  
              variant="ghost"  
              size="sm"  
              className="h-8 w-8 p-0 text-destructive hover:text-destructive"  
              onClick={() => removeMaterial(material.id)}  
            >  
              <Minus className="h-4 w-4" />  
            </Button>  
          </div>  
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 items-start">  
            <div className="space-y-2">  
              <Label htmlFor={`material-fileName-${material.id}`}>  
                Material Name<span className="text-red-500">*</span>  
              </Label>  
              <Input  
                id={`material-fileName-${material.id}`}  
                value={material.fileName}  
                onChange={(e) => updateMaterial(material.id, { fileName: e.target.value })}  
                placeholder="e.g., HTML Basics PDF"  
              />  
            </div>  
            
            <div className="space-y-2">  
              <Label>  
                Material Source  
              </Label>  
              <Tabs defaultValue="link" className="w-full">  
                <TabsList className="grid grid-cols-2 mb-2">  
                  <TabsTrigger value="link">Provide Link</TabsTrigger>  
                </TabsList>  
                
                <TabsContent value="file" className="space-y-2">  
                  <div className="flex gap-2">  
                    <Input  
                      value={material.file ? material.file.name : ''}  
                      readOnly  
                      placeholder="Select a file to upload"  
                      className="flex-1"  
                    />  
                    <input  
                      type="file"  
                      id={`material-file-${material.id}`}  
                      className="hidden"  
                      onChange={(e) => handleFileChange(e, material.id)}  
                      ref={el => fileInputRefs.current[material.id] = el}  
                    />  
                    <Button  
                      type="button"  
                      variant="outline"  
                      onClick={() => fileInputRefs.current[material.id]?.click()}  
                    >  
                      <Upload className="h-4 w-4" />  
                    </Button>  
                  </div>  
                </TabsContent>  
                
                <TabsContent value="link" className="space-y-2">  
                  <div className="flex gap-2">  
                    <Input  
                      id={`material-link-${material.id}`}  
                      value={material.link}  
                      onChange={(e) => updateMaterial(material.id, {   
                        link: e.target.value ,  
                        file: undefined  
                      })}  
                      placeholder="https://example.com/document.pdf"  
                      className="flex-1"  
                    />  
                    <Button  
                      type="button"  
                      variant="outline"  
                      className="flex-shrink-0"  
                      disabled  
                    >  
                      <LinkIcon className="h-4 w-4" />  
                    </Button>  
                  </div>  
                </TabsContent>  
              </Tabs>  
            </div>  
          </div>  
        </div>  
      ))}  
      
      {materials.length === 0 && (  
        <div className="text-center py-8 border rounded-md bg-gray-50">  
          <p className="text-gray-500 mb-2">No materials added yet.</p>  
          <Button   
            type="button"   
            variant="outline"   
            onClick={addMaterial}  
          >  
            <Plus className="mr-1 h-4 w-4" />  
            Add First Material  
          </Button>  
        </div>  
      )}  
    </div>  
  );  
};  

export default MaterialsList;  